/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  Table,
  Select,
  Form,
  Input,
  notification,
  Upload,
  Tooltip,
  Skeleton,
  DatePicker,
  Spin,
} from 'antd';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs';
import { uploadConsumablesAndMaterialFile } from '../redux/consumable/action';
import get from 'lodash/get';
import { downloadDataAsExcel } from '../utils/downloadExcelUtils';
import {
  addMaterialConsumption,
  fetchMaterialConsumption,
  deleteMaterilaConsumption,
  updateMaterialConsumption,
  fetchMaterialCategories,
} from '../redux/materialConsumption/action';
import moment from 'moment';
import { Button as CrceButton, Checkbox } from '@crc-evans/ui-components';
import {
  Upload as UploadIcon,
  Trash2,
  Pencil,
  FileText,
  Plus,
  Download,
  Package,
  Search,
  X,
} from 'lucide-react';
import './MaterialConsumption.css';
import '../pages/technical/HeatProfilePage.css';
import './DPR/DPRSettingsPipelinesTab.css';
import { useHeatStyleColumnFacets } from './technical/heatStyleColumnFacets';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import { wrapText } from '../utils/pdfUtils';
import { ProjectTabTableEmpty } from './project/ProjectTabTableEmpty';
import { generateCombinedPdfReport } from '../utils/combinedPdfReportUtils';
import axiosConfig from '../services/axiosConfig';
const { Option } = Select;

const MATERIAL_FACET_KEYS = [
  'category',
  'name',
  'productCode',
  'batchNumber',
  '__totalMobStr',
  '__usedStr',
  '__remainingStr',
  '__safetyStr',
  'unit',
  '__expiryDisplay',
  '__certLabel',
];

const MATERIAL_COLUMN_FILTERS_EMPTY = MATERIAL_FACET_KEYS.reduce((acc, k) => {
  acc[k] = '';
  return acc;
}, {});

/** Row-selection column width (must be included in `scroll.x` or headers/body misalign and clip icons). */
const MC_SELECTION_COLUMN_WIDTH = 60;

/** Pixel widths for Material table — keep in sync with `scroll.x` below. */
const MC_COL_WIDTH = {
  sn: 72,
  /* Wide enough for full header label + sort + filter (funnel must not clip) */
  category: 208,
  name: 200,
  productCode: 172,
  batchNumber: 168,
  totalMobStockQty: 172,
  totalQuantityUsed: 172,
  quantityRemaining: 212,
  safetyStock: 156,
  unit: 108,
  expiryDate: 136,
  certificate: 120,
  action: 120,
};

const MC_TABLE_SCROLL_X =
  MC_SELECTION_COLUMN_WIDTH +
  Object.values(MC_COL_WIDTH).reduce((sum, w) => sum + w, 0);

function enrichMaterialRowForFacets(row) {
  if (!row || typeof row !== 'object') return row;
  const expiry = row.expiryDate ? moment.utc(row.expiryDate).format('DD-MMM-YYYY') : '—';
  const hasCert = row.certificate != null && JSON.stringify(row.certificate) !== '{}';
  return {
    ...row,
    __totalMobStr: row.totalMobStockQty != null && row.totalMobStockQty !== '' ? String(row.totalMobStockQty) : '—',
    __usedStr: row.totalQuantityUsed != null && row.totalQuantityUsed !== '' ? String(row.totalQuantityUsed) : '—',
    __remainingStr:
      row.quantityRemaining != null && row.quantityRemaining !== '' ? String(row.quantityRemaining) : '—',
    __safetyStr: row.safetyStock != null && row.safetyStock !== '' ? String(row.safetyStock) : '—',
    __expiryDisplay: expiry,
    __certLabel: hasCert ? 'Yes' : 'No',
  };
}

const getMcMaterialRowKey = (record) => {
  if (record == null) return undefined;
  if (record.id !== undefined && record.id !== null) return record.id;
  if (record.key !== undefined && record.key !== null) return record.key;
  return undefined;
};

/** Rows without a certificate cannot be selected (combined PDF requires attachment). */
const isMcMaterialRowSelectable = (record) => {
  if (!record) return false;
  const c = record.certificate;
  return !(c == null || JSON.stringify(c) === '{}');
};

/** Cognito groups when Redux userAccessList is empty (e.g. modern dashboard TabList). */
const resolveUserGroups = (userAccessList) => {
  const fromProps = Array.isArray(userAccessList) ? userAccessList : [];
  if (fromProps.length > 0) return fromProps;
  try {
    const raw = localStorage.getItem('UserGroup');
    if (raw == null || raw === '') return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return String(raw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
  } catch {
    return [];
  }
};

const isGlobalAdminGroup = (groups) =>
  Array.isArray(groups) &&
  groups.some((g) => {
    const n = String(g || '').toLowerCase();
    return n === 'admin' || n === 'super-admin' || n === 'administrator';
  });

/** Explicit false => no write; true/undefined/missing key => allow (legacy APIs omit keys). */
const hasMaterialConsumptionTabWrite = (projectTeamMembersAccessList) => {
  const tabs = projectTeamMembersAccessList?.[0]?.tabPermissions;
  if (!tabs || typeof tabs !== 'object') return true;
  const w =
    tabs['Material Consumption']?.write ??
    tabs['Material consumption']?.write ??
    tabs.MaterialConsumption?.write ??
    tabs['material-consumption']?.write;
  if (w === false) return false;
  return true;
};

// Set worker path for PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

function formatMcBulkFileSize(bytes) {
  if (bytes == null || typeof bytes !== 'number' || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MaterialConsumption = (props) => {
  const {
    selectedProject,
    isDsr,
    onFetchMaterialConsumption,
    materialConsumption,
    isMaterialConsumptionsLoading,
    onAddMaterialConsumption,
    onUploadConsumablesAndMaterialFiles,
    isMaterialConsumptionDeleteLoading,
    onDeleteMaterialConsumption,
    isUploadConsumablesAndMaterialConsumptions,
    isMaterialConsumptionsUpdating,
    onUpdateMaterialConsumption,
    categoryData,
    onFetchCategories,
    projectTeamMembersAccessList,
    userAccessList,
  } = props;

  const [showAddModal, setShowAddModal] = useState(false);
  const [bulkUplaodModal, setBulkUplaodModal] = useState(false);
  const [editDetailsForm] = Form.useForm();
  const [name, setName] = useState('');
  const [productCode, setProductCode] = useState('');
  const [fileList, setFileList] = useState([]);
  const [disableFileUpload, setDisableFileUpload] = useState(false);
  const [certificateUpload, setCertificateUpload] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedData, setSelectedData] = useState('');
  const [editValue, setEditValue] = useState(false);
  const [materialConsumptionRecord, setMaterialConsumptionRecord] = useState({});
  const [searchKeyExist, setSearchKeyExist] = useState('');
  const [mcPage, setMcPage] = useState(1);
  const [mcPageSize, setMcPageSize] = useState(10);
  const [expiryDate, setExpiryDate] = useState(null);
  const [certificate, setCertificate] = useState('');
  const [materialCategory, setMaterialCategory] = useState('');
  const [unitDataList, setUnitDataList] = useState([]);
  const [categoryDataList, setCategoryDataList] = useState([]);
  const [filteredCatg, setFilteredCatg] = useState([]);
  const [filteredProductCode, setFilteredProductCode] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [pdfGenerationLoading, setPdfGenerationLoading] = useState(false);
  const [heatColumnFilters, setHeatColumnFilters] = useState(() => ({
    ...MATERIAL_COLUMN_FILTERS_EMPTY,
  }));
  const [materialSort, setMaterialSort] = useState({
    key: 'category',
    desc: false,
  });

  /* true = user cannot mutate (Add / Bulk / Edit / Delete). Named isWriteAccess historically — means "read-only". */
  const isButtonDisabled = () => {
    const groups = resolveUserGroups(userAccessList);
    if (isGlobalAdminGroup(groups)) return false;
    if (projectTeamMembersAccessList?.[0]?.isGuestUser) return true;
    if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !hasMaterialConsumptionTabWrite(projectTeamMembersAccessList)
    ) {
      return true;
    }
    return false;
  };
  const isWriteAccess = isButtonDisabled();

  useEffect(() => {
    if (selectedProject && selectedProject.id) {
      onFetchMaterialConsumption(selectedProject);
      onFetchCategories();
    }
  }, [selectedProject]);

  useEffect(() => {
    setMcPage(1);
    setSearchKeyExist('');
    setHeatColumnFilters({ ...MATERIAL_COLUMN_FILTERS_EMPTY });
    setMaterialSort({ key: 'category', desc: false });
  }, [selectedProject?.id]);

  useEffect(() => {
    if (!materialConsumption?.length) {
      setSelectedRows([]);
    }
  }, [materialConsumption?.length]);

  useEffect(() => {
    if (categoryData.length) {
      const uniqueCategory = new Set();
      const categoryArr = categoryData.filter((item) => {
        const isDuplicate = uniqueCategory.has(item.category);
        uniqueCategory.add(item.category);
        return !isDuplicate;
      });
      setCategoryDataList(categoryArr);
    } else {
      setCategoryDataList([]);
    }
  }, [categoryData]);

  useEffect(() => {
    if (categoryData.length) {
      const uniqueUnit = new Set();
      const unitArr = categoryData.filter((item) => {
        const isDuplicate = uniqueUnit.has(item.unit);
        uniqueUnit.add(item.unit);
        return !isDuplicate;
      });
      const sortedUnitArr = unitArr.sort((a, b) => {
        return a.unit.localeCompare(b.unit);
      });

      setUnitDataList(sortedUnitArr);
    } else {
      setUnitDataList([]);
    }
  }, [categoryData]);

  const truncateTableData = (data) => {
    return (
      <Tooltip
        placement="topLeft"
        title={data}
      >
        {data}
      </Tooltip>
    );
  };

  const showEditModal = async (record) => {
    let editData = null;
    if (record != null) {
      editData = {
        id: record.id,
        category: record.category,
        name: record.name,
        batchNumber: record.batchNumber,
        productCode: record.productCode,
        totalMobStockQty: record.totalMobStockQty,
        safetyStock: record.safetyStock,
        unit: record.unit,
        expiryDate: record.expiryDate ? moment(record.expiryDate, 'YYYY-MM-DD') : '',
      };
    }
    setEditValue(true);
    editDetailsForm.setFieldsValue(editData);
    setMaterialCategory(record.category);
    setMaterialConsumptionRecord(record);
    setShowAddModal(true);
    setCertificate(record?.certificate?.fileURL);
  };

  const capitalizeWords = (str) => {
    let words = str.split(' ');
    for (let i = 0; i < words.length; i++) {
      words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
    }
    return words.join(' ');
  };

  const formatFileSize = (bytes) => {
    if (!bytes || Number.isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const selectedCertificateFile = fileList?.[0] || null;
  const selectedCertificateFileName = selectedCertificateFile?.name || '';
  const selectedCertificateFileSize = selectedCertificateFile?.size
    ? formatFileSize(selectedCertificateFile.size)
    : '';

  const handleRemoveSelectedCertificate = () => {
    setFileList([]);
    setDisableFileUpload(false);
    editDetailsForm.setFieldsValue({ certificate: undefined });
  };

  /** Toolbar search — multi-token across visible columns (same as Consumables / Equipment). */
  const materialSearchFiltered = useMemo(() => {
    const needle = searchKeyExist.trim().toLowerCase();
    if (!needle) return materialConsumption;
    const tokens = needle.split(/\s+/).filter(Boolean);
    return materialConsumption.filter((row) => {
      const parts = [
        row.category,
        row.name,
        row.productCode,
        row.batchNumber,
        row.unit,
        row.totalMobStockQty,
        row.totalQuantityUsed,
        row.quantityRemaining,
        row.safetyStock,
        row.expiryDate,
      ];
      const haystack = parts
        .map((x) => (x === undefined || x === null ? '' : String(x)).toLowerCase())
        .join(' ');
      return tokens.every((t) => haystack.includes(t));
    });
  }, [materialConsumption, searchKeyExist]);

  const materialFacetSource = useMemo(
    () => materialSearchFiltered.map((r) => enrichMaterialRowForFacets({ ...r })),
    [materialSearchFiltered]
  );

  const handleMaterialHeatFilterChange = useCallback((key, value) => {
    setHeatColumnFilters((prev) => ({ ...prev, [key]: value }));
    setMcPage(1);
  }, []);

  const materialFacetRef = useRef([]);
  materialFacetRef.current = materialFacetSource;
  const fetchMaterialFacetRows = useCallback(async () => {
    const list = materialFacetRef.current;
    return Array.isArray(list) ? [...list] : [];
  }, []);

  const materialFacetSearchCriteria = useMemo(() => ({}), []);

  const { applyFacetClientFilter, renderTextColumnFilterTrigger } = useHeatStyleColumnFacets({
    enabled: Boolean(selectedProject?.id),
    filterableColumns: MATERIAL_FACET_KEYS,
    listCount: materialFacetSource.length,
    projectList: materialFacetSource,
    searchCriteria: materialFacetSearchCriteria,
    columnFilters: heatColumnFilters,
    onColumnFilterChange: handleMaterialHeatFilterChange,
    fetchFacetRows: fetchMaterialFacetRows,
  });

  const lcMat = (v) => (v == null ? '' : String(v)).trim().toLowerCase();

  const filteredAndFacetedMaterial = useMemo(() => {
    let rows = [...materialFacetSource];
    MATERIAL_FACET_KEYS.forEach((key) => {
      const raw = (heatColumnFilters[key] || '').trim();
      if (!raw) return;
      const q = lcMat(raw);
      rows = rows.filter((row) => lcMat(get(row, key)).includes(q));
    });
    return applyFacetClientFilter(rows);
  }, [materialFacetSource, heatColumnFilters, applyFacetClientFilter]);

  const handleMaterialSortClick = useCallback((field) => {
    setMaterialSort((prev) =>
      prev.key === field ? { ...prev, desc: !prev.desc } : { key: field, desc: false }
    );
  }, []);

  const renderMaterialSortCaret = useCallback(
    (field) =>
      materialSort.key === field ? (
        materialSort.desc ? (
          <CaretDownOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        ) : (
          <CaretUpOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        )
      ) : (
        <CaretUpOutlined
          className="equipment-type-sort-dir-icon doc-control-sort-caret-inactive"
          aria-hidden
        />
      ),
    [materialSort.key, materialSort.desc]
  );

  const renderMaterialHeader = useCallback(
    (labelNode, dataIndex, sortField, { filterable = true, headerAlign = 'center' } = {}) => {
      if (!filterable) {
        return (
          <div className={`dpr-pipelines-header-static dpr-pipelines-header-static--${headerAlign}`}>
            {labelNode}
          </div>
        );
      }
      return (
        <div className={`dpr-pipelines-heat-header dpr-pipelines-heat-header--${headerAlign}`}>
          <div className="column-header-text-wrapper spares-column-header-sortable dpr-pipelines-heat-title-row">
            <span
              className="dpr-pipelines-heat-label dpr-pipelines-heat-label--sortable"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleMaterialSortClick(sortField);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleMaterialSortClick(sortField);
                }
              }}
            >
              {labelNode}
            </span>
            <span
              className="custom-sort-icon"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleMaterialSortClick(sortField);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleMaterialSortClick(sortField);
                }
              }}
            >
              {renderMaterialSortCaret(sortField)}
            </span>
            {renderTextColumnFilterTrigger(dataIndex)}
          </div>
        </div>
      );
    },
    [handleMaterialSortClick, renderMaterialSortCaret, renderTextColumnFilterTrigger]
  );

  const sortedMaterialDisplayList = useMemo(() => {
    const rows = [...filteredAndFacetedMaterial];
    const cmpStr = (a, b, key) =>
      String(get(a, key) ?? '')
        .trim()
        .toLowerCase()
        .localeCompare(String(get(b, key) ?? '').trim().toLowerCase(), undefined, {
          sensitivity: 'base',
          numeric: true,
        });
    const cmpNum = (a, b, key) => {
      const na = Number(get(a, key));
      const nb = Number(get(b, key));
      const aOk = Number.isFinite(na);
      const bOk = Number.isFinite(nb);
      if (!aOk && !bOk) return 0;
      if (!aOk) return 1;
      if (!bOk) return -1;
      return na - nb;
    };
    const tieBreak = (a, b) => {
      const idA = a?.id != null ? Number(a.id) : NaN;
      const idB = b?.id != null ? Number(b.id) : NaN;
      if (Number.isFinite(idA) && Number.isFinite(idB) && idA !== idB) return idA < idB ? -1 : 1;
      return String(a?.id ?? '').localeCompare(String(b?.id ?? ''), undefined, { numeric: true });
    };
    const { key: sortKey, desc: sortDesc } = materialSort;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'category':
        case 'name':
        case 'productCode':
        case 'batchNumber':
        case '__totalMobStr':
        case '__usedStr':
        case '__remainingStr':
        case '__safetyStr':
        case 'unit':
        case '__expiryDisplay':
        case '__certLabel':
          cmp = cmpStr(a, b, sortKey);
          break;
        case 'totalMobStockQty':
        case 'totalQuantityUsed':
        case 'quantityRemaining':
        case 'safetyStock':
          cmp = cmpNum(a, b, sortKey);
          break;
        default:
          cmp = 0;
      }
      if (cmp === 0) cmp = tieBreak(a, b);
      return sortDesc ? -cmp : cmp;
    });
    return rows;
  }, [filteredAndFacetedMaterial, materialSort]);

  const filteredMaterialConsumption = sortedMaterialDisplayList;

  const paginatedMaterialConsumption = useMemo(() => {
    const start = (mcPage - 1) * mcPageSize;
    return filteredMaterialConsumption.slice(start, start + mcPageSize);
  }, [filteredMaterialConsumption, mcPage, mcPageSize]);

  useEffect(() => {
    const total = filteredMaterialConsumption.length;
    const maxPage = Math.max(1, Math.ceil(total / mcPageSize) || 1);
    if (mcPage > maxPage) setMcPage(maxPage);
  }, [filteredMaterialConsumption.length, mcPage, mcPageSize]);

  const handleMcSearchChange = (event) => {
    setSearchKeyExist(event.target.value);
    setMcPage(1);
  };

  const createExcelTemplate = () => {
    const workbook = new ExcelJS.Workbook();

    // Material Consumption must be the first worksheet so Excel opens on these columns by default.
    const materialConsumptionSheet = workbook.addWorksheet('Material Consumption');
    const mcHeadRow = materialConsumptionSheet.getRow(1);
    mcHeadRow.height = 60;
    materialConsumptionSheet.columns = [
      { header: 'Material Category', key: 'category', width: 30 },
      { header: 'Name', key: 'name', width: 20 },
      { header: 'Product Code', key: 'productCode', width: 20 },
      { header: 'Batch Number', key: 'batchNumber', width: 20 },
      { header: 'Initial Quantity', key: 'totalMobStockQty', width: 20 },
      { header: 'Safety Stock', key: 'safetyStock', width: 16 },
      { header: 'Unit', key: 'unit', width: 16 },
      { header: 'Expiry Date', key: 'expiryDate', width: 22 },
    ];

    const consumableSheet = workbook.addWorksheet('Consumables');
    const headerRow = consumableSheet.getRow(1);
    headerRow.height = 60;
    consumableSheet.columns = [
      { header: 'Consumable Category', key: 'category', width: 30 },
      { header: 'Name', key: 'name', width: 20 },
      { header: 'Product Code', key: 'productCode', width: 20 },
      { header: 'Initial Quantity', key: 'quantityInStock', width: 20 },
      { header: 'Safety Stock', key: 'safetyStock', width: 16 },
      { header: 'Unit', key: 'unit', width: 16 },
      { header: 'Criticality', key: 'criticality', width: 22 },
    ];
    const critColIndex = 7;
    const applyCriticalityValidation = (sheet) => {
      for (let r = 2; r <= 500; r += 1) {
        sheet.getRow(r).getCell(critColIndex).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: ['"Critical,Medium,Normal"'],
          showErrorMessage: true,
          errorTitle: 'Criticality',
          error: 'Choose Critical, Medium, or Normal.',
        };
      }
    };
    applyCriticalityValidation(consumableSheet);

    return workbook.xlsx.writeBuffer();
  };

  const handleDownloadTemplate = async () => {
    const data = await createExcelTemplate();
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template.xlsx';
    link.click();
  };

  const handleDownloadData = async () => {
    const columns = [
      { header: 'Material Category', key: 'category', width: 20 },
      { header: 'Name', key: 'name', width: 20 },
      { header: 'Product Code', key: 'productCode', width: 20 },
      { header: 'Batch Number', key: 'batchNumber', width: 20 },
      { header: 'Initial Quantity', key: 'totalMobStockQty', width: 15 },
      { header: 'Used Quantity', key: 'totalQuantityUsed', width: 15 },
      { header: 'Quantity Available', key: 'quantityRemaining', width: 18 },
      { header: 'Safety Stock', key: 'safetyStock', width: 15 },
      { header: 'Unit', key: 'unit', width: 12 },
      { header: 'Expiry Date', key: 'expiryDate', width: 15 },
      { header: 'File URL', key: 'certificateURL', width: 30 },
    ];

    const dataFormatter = (record) => {
      return {
        ...record,
        expiryDate: record.expiryDate ? moment.utc(record.expiryDate).format('YYYY-MM-DD') : '',
        certificateURL: record?.certificate?.fileURL || '',
      };
    };

    const cellStyler = (worksheet, rowNumber, columnKey, cell, rowData) => {
      // Apply "button-like" style to the File URL column
      if (columnKey === 'certificateURL' && rowData?.certificateURL) {
        const url = rowData.certificateURL;
        cell.value = { text: 'Download File', hyperlink: url };
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF0070C0' }, // Blue fill
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    };

    await downloadDataAsExcel({
      data: materialConsumption,
      fileName: 'List of Material Consumption',
      columns,
      sheetName: 'Material Consumption',
      dataFormatter,
      cellStyler,
      setDownloadLoading,
    });
  };

  // Handle Generate Combined PDF Report
  const handleGeneratePdf = async () => {
    setPdfGenerationLoading(true);

    try {
      // Filter selected rows to only include those with certificates
      let rowsWithCertificates = selectedRows.filter((row) => {
        return row && row.certificate !== null && JSON.stringify(row.certificate) !== '{}';
      });

      if (rowsWithCertificates.length === 0) {
        notification.warning({
          message: 'Please select at least one material consumption with a certificate to generate the report',
        });
        setPdfGenerationLoading(false);
        return;
      }

      // Sort by Material Category, then Name, then Product Code for consistent ordering
      rowsWithCertificates = [...rowsWithCertificates].sort((a, b) => {
        const categoryA = (a.category || '').toLowerCase();
        const categoryB = (b.category || '').toLowerCase();
        if (categoryA !== categoryB) {
          return categoryA.localeCompare(categoryB);
        }
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        if (nameA !== nameB) {
          return nameA.localeCompare(nameB);
        }
        const productCodeA = (a.productCode || '').toLowerCase();
        const productCodeB = (b.productCode || '').toLowerCase();
        return productCodeA.localeCompare(productCodeB);
      });

      // Get certificate URLs from the certificate.fileURL field
      const getCertificateUrl = async (row) => {
        if (row?.certificate?.fileURL) {
          return row.certificate.fileURL;
        }
        return null;
      };

      // Render PDF page as image function
      const renderPdfPageAsImage = async (pdfUrl) => {
        try {
          const loadingTask = pdfjsLib.getDocument(pdfUrl);
          const pdf = await loadingTask.promise;
          const page = await pdf.getPage(1);
          // High-quality raster rendering for combined PDF output
          const viewport = page.getViewport({ scale: 2.5 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          await page.render({ canvasContext: context, viewport }).promise;
          return canvas.toDataURL('image/png');
        } catch (error) {
          console.error('Error rendering PDF page:', error);
          return null;
        }
      };

      // Table configuration for Material Consumption
      const getColumnWidths = (font, tableWidth, padding, fontSize) => {
        const seqWidth = font.widthOfTextAtSize('S/N', fontSize) + padding;
        const categoryWidth = font.widthOfTextAtSize('Material Category', fontSize) + padding;
        const nameWidth = font.widthOfTextAtSize('Name', fontSize) + padding;
        const productCodeWidth = font.widthOfTextAtSize('Product Code', fontSize) + padding;
        const batchNumberWidth = font.widthOfTextAtSize('Batch Number', fontSize) + padding;
        const expiryDateWidth = font.widthOfTextAtSize('Expiry Date', fontSize) + padding;

        // Calculate remaining width for Name (largest column)
        const totalFixedWidth = seqWidth + categoryWidth + productCodeWidth + batchNumberWidth + expiryDateWidth;
        const nameAdjustedWidth = Math.max(nameWidth, tableWidth - totalFixedWidth);

        return {
          seq: seqWidth,
          category: categoryWidth,
          name: nameAdjustedWidth,
          productCode: productCodeWidth,
          batchNumber: batchNumberWidth,
          expiryDate: expiryDateWidth,
        };
      };

      const formatRowData = (row, index) => {
        return [
          (index + 1).toString(),
          row?.category || '-',
          row?.name || '-',
          row?.productCode || '-',
          row?.batchNumber || '-',
          row?.expiryDate ? moment.utc(row.expiryDate).format('DD-MMM-YYYY') : '-',
        ];
      };

      // Generate PDF using common utility
      const result = await generateCombinedPdfReport({
        selectedRows: rowsWithCertificates,
        renderPdfPageAsImage,
        getCertificateUrl,
        reportConfig: {
          heading: 'Material Certificates',
          section1Title: '1.0 Material',
          section2Title: '2.0 Material Certificates',
          fileName: 'Combined Material Report.pdf',
        },
        tableConfig: {
          headers: ['S/N', 'Material Category', 'Name', 'Product Code', 'Batch Number', 'Expiry Date'],
          getColumnWidths,
          formatRowData,
          styling: {
            rowHeight: 35,
            fontSize: 9,
            headerFontSize: 10,
            enableTextWrapping: true,
            wrapText,
          },
        },
      });

      // Show notification to user
      if (result.successCount > 0) {
        notification.success({
          message: 'PDF Generated Successfully',
          description: `Merged ${result.successCount} certificate(s) with ${result.totalPages} total page(s).`,
          duration: 5,
        });
      }
      if (result.failCount > 0) {
        notification.warning({
          message: 'Some PDFs Failed to Merge',
          description: `${result.failCount} certificate(s) could not be merged.`,
          duration: 5,
        });
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      notification.error({
        message: 'Error Generating PDF',
        description: get(error, ['response', 'data', 'message'], error.message || 'An error occurred while generating the PDF.'),
        duration: 5,
      });
    } finally {
      setPdfGenerationLoading(false);
    }
  };

  const showModal = () => {
    if (selectedProject) {
      setShowAddModal(true);
    } else {
      notification.warn({ message: 'Select Project to add data.' });
    }
  };

  const showBulkUploadModal = () => {
    if (selectedProject) {
      setFileList([]);
      setDisableFileUpload(false);
      setBulkUplaodModal(true);
    } else {
      notification.warn({ message: 'Select Project to add data.' });
    }
  };

  const handleCancel = () => {
    editDetailsForm.resetFields(); //resetting input fields
    setFileList([]);
    setShowAddModal(false);
    setEditValue(false);
    setCertificateUpload(false);
    setFilteredCatg([]);
    setName('');
  };

  const handleAdd = async () => {
    editDetailsForm.validateFields().then(async (data) => {
      if (
        data.category &&
        data.name &&
        data.productCode &&
        data.totalMobStockQty.toString() &&
        data.batchNumber &&
        data.safetyStock &&
        data.unit
      ) {
        let payload = {
          name: data.name,
          category: materialCategory,
          batchNumber: data.batchNumber,
          productCode: data.productCode,
          totalMobStockQty: data.totalMobStockQty,
          projectId: selectedProject.id,
          safetyStock: data.safetyStock,
          unit: data.unit,
        };

        // Conditionally include expiryDate if it's available
        if (data.expiryDate) {
          payload.expiryDate = moment(data.expiryDate).format('YYYY-MM-DD');
        }

        // Conditionally include certificate if a file has been uploaded
        if (fileList.length > 0) {
          payload.certificate = fileList;
        }
        if (editValue) {
          let res = await onUpdateMaterialConsumption({
            data: payload,
            materialConsumptionRecord: materialConsumptionRecord,
            selectedProject: selectedProject,
          });
          setFileList([]);
        } else {
          let res = await onAddMaterialConsumption(payload);
          if (!isMaterialConsumptionsLoading) {
            setBulkUplaodModal(false);
            setShowAddModal(false);
            editDetailsForm.resetFields(); // Resetting input fields
            setFileList([]);
            setFilteredCatg([]);
          }
        }
      } else {
        notification.warning({
          message: 'Fill all the required fields!',
        });
      }
    });
  };

  const handleCancelUploadFile = () => {
    setFileList([]);
    setBulkUplaodModal(false);
    setDisableFileUpload(false);
  };

  useEffect(() => {
    if (!isUploadConsumablesAndMaterialConsumptions) {
      setFileList([]);
      setBulkUplaodModal(false);
      setDisableFileUpload(false);
      setEditValue(false);
    }
  }, [isUploadConsumablesAndMaterialConsumptions]);

  useEffect(() => {
    if (!isMaterialConsumptionsUpdating) {
      editDetailsForm.resetFields();
      setShowAddModal(false);
      setEditValue(false);
      setMaterialConsumptionRecord(null);
    }
  }, [isMaterialConsumptionsUpdating]);

  const fileProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
      setDisableFileUpload(false);
    },
    beforeUpload: (file) => {
      //for multiple upload
      // setFileList([...fileList, file]);
      // if (fileList.length > 0) {
      //     notification.warn({ message: 'Upload only one file at a time.' })
      // } else {
      // for single file upload
      setFileList([file]);
      setDisableFileUpload(true);
      // }
      return false;
    },
    fileList,
  };

  const filePropsForOne = {
    onRemove: () => {
      setFileList([]);
      setDisableFileUpload(false);
    },
    beforeUpload: (file) => {
      setFileList([file]);
      setDisableFileUpload(true);
      return false;
    },
    fileList,
  };

  const handleUploadFile = async () => {
    if (fileList.length > 0) {
      await onUploadConsumablesAndMaterialFiles({ fileList, projectId: selectedProject.id });
    } else {
      notification.warn({ message: 'Please select a file to upload.' });
    }
  };

  const showDeleteModal = (record) => {
    setIsDeleteModalVisible(true);
    setSelectedData(record);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setSelectedData('');
  };

  const handleDeleteOk = async () => {
    selectedData.projectId = selectedProject.id;
    await onDeleteMaterialConsumption({ id: selectedData.id, projectId: selectedProject.id });
  };
  useEffect(() => {
    if (!isMaterialConsumptionDeleteLoading) {
      setIsDeleteModalVisible(false);
      setSelectedData('');
    }
  }, [isMaterialConsumptionDeleteLoading]);

  const onChangeCategory = async (value) => {
    editDetailsForm.setFieldsValue({ name: '', productCode: '' });
    setName('');
    setProductCode('');
    if (value) {
      setMaterialCategory(value);
      var categoryArr = categoryData && categoryData.filter((item) => item.category == value);

      // Use a Set to remove duplicates based on description
      const uniqueName = new Set();
      categoryArr = categoryArr.filter((item) => {
        const isDuplicate = uniqueName.has(item.name);
        uniqueName.add(item.name);
        return !isDuplicate;
      });

      setFilteredCatg(categoryArr);
    } else {
      setFilteredCatg([]);
    }
  };

  const onChangeName = async (value) => {
    editDetailsForm.setFieldsValue({ productCode: '' });
    setProductCode('');
    if (value) {
      setName(value);
      var categoryArr = categoryData && categoryData.filter((item) => item.name == value);
      // Use a Set to remove duplicates based on productCode
      const uniqueProductCode = new Set();
      categoryArr = categoryArr.filter((item) => {
        const isDuplicate = uniqueProductCode.has(item.productCode);
        uniqueProductCode.add(item.productCode);
        return !isDuplicate;
      });
      setFilteredProductCode(categoryArr);
    } else {
      setFilteredProductCode([]);
    }
  };

  const mcTableBusy = isMaterialConsumptionsLoading;

  const selectableMcRows = useMemo(
    () => filteredMaterialConsumption.filter(isMcMaterialRowSelectable),
    [filteredMaterialConsumption],
  );

  const selectedMaterialKeys = useMemo(
    () =>
      selectedRows
        .map((r) => getMcMaterialRowKey(r))
        .filter((k) => k !== undefined && k !== null),
    [selectedRows],
  );

  const selectedMcKeysSet = useMemo(() => new Set(selectedMaterialKeys), [selectedMaterialKeys]);

  const allSelectableMcSelected =
    selectableMcRows.length > 0 &&
    selectableMcRows.every((r) => {
      const k = getMcMaterialRowKey(r);
      return k !== undefined && k !== null && selectedMcKeysSet.has(k);
    });

  const materialConsumptionRowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedMaterialKeys,
      onChange: (_keys, rowsFromAnt) => {
        setSelectedRows(rowsFromAnt);
      },
      columnWidth: MC_SELECTION_COLUMN_WIDTH,
      columnTitle: (
        <div className="mc-selection-header">
          <Checkbox
            checked={allSelectableMcSelected}
            disabled={selectableMcRows.length === 0 || mcTableBusy}
            onChange={(nextChecked) => {
              const selectableKeySet = new Set(
                selectableMcRows
                  .map((r) => getMcMaterialRowKey(r))
                  .filter((k) => k !== undefined && k !== null),
              );
              setSelectedRows((prev) => {
                if (!nextChecked) {
                  return prev.filter((r) => {
                    const k = getMcMaterialRowKey(r);
                    return k === undefined || k === null || !selectableKeySet.has(k);
                  });
                }
                const prevKeys = new Set(
                  prev.map((r) => getMcMaterialRowKey(r)).filter((k) => k !== undefined && k !== null),
                );
                const merged = [...prev];
                selectableMcRows.forEach((r) => {
                  const k = getMcMaterialRowKey(r);
                  if (k !== undefined && k !== null && !prevKeys.has(k)) {
                    merged.push(r);
                    prevKeys.add(k);
                  }
                });
                return merged;
              });
            }}
            borderColor="#00C9A7"
            tickColor="#00C9A7"
          />
        </div>
      ),
      renderCell: (checked, record) => (
        <div
          className="mc-selection-cell"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <Checkbox
            checked={checked}
            disabled={mcTableBusy || !isMcMaterialRowSelectable(record)}
            onChange={(nextChecked) => {
              const rowKeyVal = getMcMaterialRowKey(record);
              if (rowKeyVal === undefined || rowKeyVal === null) return;
              setSelectedRows((prev) => {
                const has = prev.some((r) => getMcMaterialRowKey(r) === rowKeyVal);
                if (nextChecked && !has) return [...prev, record];
                if (!nextChecked && has) return prev.filter((r) => getMcMaterialRowKey(r) !== rowKeyVal);
                return prev;
              });
            }}
            borderColor="#00C9A7"
            tickColor="#00C9A7"
          />
        </div>
      ),
    }),
    [
      selectedMaterialKeys,
      allSelectableMcSelected,
      selectableMcRows,
      mcTableBusy,
    ],
  );

  const columns = useMemo(
    () => [
      {
        title: () =>
          renderMaterialHeader('#', null, null, { filterable: false, headerAlign: 'center' }),
        key: 'sn',
        width: MC_COL_WIDTH.sn,
        align: 'center',
        className: 'sn-column',
        render: (_text, _record, index) => (mcPage - 1) * mcPageSize + index + 1,
      },
      {
        title: () =>
          renderMaterialHeader('MATERIAL CATEGORY', 'category', 'category', { headerAlign: 'left' }),
        dataIndex: 'category',
        key: 'category',
        width: MC_COL_WIDTH.category,
        align: 'left',
      },
      {
        title: () => renderMaterialHeader('NAME', 'name', 'name', { headerAlign: 'left' }),
        dataIndex: 'name',
        key: 'name',
        align: 'left',
        ellipsis: { showTitle: false },
        width: MC_COL_WIDTH.name,
        render: (value) => <span>{value != null && value !== '' ? capitalizeWords(String(value)) : ''}</span>,
      },
      {
        title: () =>
          renderMaterialHeader('PRODUCT CODE', 'productCode', 'productCode', { headerAlign: 'left' }),
        dataIndex: 'productCode',
        key: 'productCode',
        align: 'left',
        ellipsis: { showTitle: false },
        width: MC_COL_WIDTH.productCode,
        render: (data) => (data != null && data !== '' ? truncateTableData(String(data).toUpperCase()) : ''),
      },
      {
        title: () =>
          renderMaterialHeader('BATCH NUMBER', 'batchNumber', 'batchNumber', { headerAlign: 'left' }),
        dataIndex: 'batchNumber',
        key: 'batchNumber',
        align: 'left',
        ellipsis: { showTitle: false },
        width: MC_COL_WIDTH.batchNumber,
        render: (data) => (data != null && data !== '' ? truncateTableData(String(data).toUpperCase()) : ''),
      },
      {
        title: () =>
          renderMaterialHeader('INITIAL QUANTITY', '__totalMobStr', 'totalMobStockQty'),
        dataIndex: 'totalMobStockQty',
        key: 'totalMobStockQty',
        width: MC_COL_WIDTH.totalMobStockQty,
        align: 'center',
      },
      {
        title: () => renderMaterialHeader('USED QUANTITY', '__usedStr', 'totalQuantityUsed'),
        dataIndex: 'totalQuantityUsed',
        key: 'totalQuantityUsed',
        width: MC_COL_WIDTH.totalQuantityUsed,
        align: 'center',
      },
      {
        title: () =>
          renderMaterialHeader('QUANTITY AVAILABLE', '__remainingStr', 'quantityRemaining'),
        dataIndex: 'quantityRemaining',
        key: 'quantityRemaining',
        width: MC_COL_WIDTH.quantityRemaining,
        align: 'center',
      },
      {
        title: () => renderMaterialHeader('SAFETY STOCK', '__safetyStr', 'safetyStock'),
        dataIndex: 'safetyStock',
        key: 'safetyStock',
        width: MC_COL_WIDTH.safetyStock,
        align: 'center',
      },
      {
        title: () => renderMaterialHeader('UNIT', 'unit', 'unit'),
        dataIndex: 'unit',
        key: 'unit',
        width: MC_COL_WIDTH.unit,
        align: 'center',
      },
      {
        title: () =>
          renderMaterialHeader('EXPIRY DATE', '__expiryDisplay', '__expiryDisplay'),
        dataIndex: 'expiryDate',
        key: 'expiryDate',
        width: MC_COL_WIDTH.expiryDate,
        align: 'center',
        render: (text) => <div>{text ? moment.utc(text).format('DD-MMM-YYYY') : ''}</div>,
      },
      {
        title: () =>
          renderMaterialHeader('CERTIFICATE', '__certLabel', '__certLabel'),
        dataIndex: 'certificate',
        key: 'certificate',
        width: 112,
        align: 'center',
        render: (_text, record) => (
          <>
            {record &&
            record.certificate !== null &&
            JSON.stringify(record.certificate) !== '{}' ? (
              <Tooltip title="View / Download report">
                <a
                  href={record?.certificate?.fileURL}
                  className="modern-action-btn pdf mc-cert-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open certificate"
                >
                  <FileText size={18} strokeWidth={1.75} />
                </a>
              </Tooltip>
            ) : (
              <Tooltip title="Report not generated">
                <span className="modern-action-btn pdf is-disabled" aria-hidden>
                  <FileText size={18} strokeWidth={1.75} />
                </span>
              </Tooltip>
            )}
          </>
        ),
      },
      {
        title: () =>
          renderMaterialHeader('ACTIONS', null, null, { filterable: false, headerAlign: 'center' }),
        key: 'action',
        dataIndex: 'action',
        width: MC_COL_WIDTH.action,
        fixed: 'right',
        align: 'center',
        render: (_text, record) => (
          <div className="mc-actions-cell">
            <Tooltip title="Edit">
              <button
                type="button"
                className="modern-action-btn mc-action-edit"
                onClick={() => {
                  if (!isWriteAccess) showEditModal(record);
                }}
                disabled={isWriteAccess}
              >
                <Pencil size={18} strokeWidth={1.75} />
              </button>
            </Tooltip>
            <Tooltip title="Delete">
              <button
                type="button"
                className="modern-action-btn mc-action-delete"
                onClick={() => {
                  if (!isWriteAccess) showDeleteModal(record);
                }}
                disabled={isWriteAccess}
              >
                <Trash2 size={18} strokeWidth={1.75} />
              </button>
            </Tooltip>
          </div>
        ),
      },
    ],
    [
      renderMaterialHeader,
      mcPage,
      mcPageSize,
      isWriteAccess,
      showEditModal,
      showDeleteModal,
      truncateTableData,
      capitalizeWords,
    ],
  );

  const materialConsumptionRowKey = (record, index) =>
    getMcMaterialRowKey(record) ?? `mc-${index}`;

  const bulkUploadAntFile = fileList[0];
  const bulkUploadRawFile = bulkUploadAntFile?.originFileObj || bulkUploadAntFile;
  const bulkUploadFileDisplayName =
    bulkUploadRawFile?.name || bulkUploadAntFile?.name || 'Selected file';
  const bulkUploadFileSizeLabel = formatMcBulkFileSize(bulkUploadRawFile?.size);

  return (
    <div className="modern-mc-page">
      {!isDsr && (
        <>
          <div className="mc-header">
            <div className="mc-header-left">
              <div className="mc-header-icon">
                <Package size={24} color="#2563eb" strokeWidth={1.75} aria-hidden />
              </div>
              <div className="mc-header-text">
                <h1 className="mc-title">Material Consumption</h1>
                <p className="mc-subtitle">Track and manage material usage for this project.</p>
              </div>
            </div>
            <div className="mc-header-right">
              <CrceButton
                label="Download template"
                variant="tertiary"
                size="small"
                icon={Download}
                onClick={handleDownloadTemplate}
              />
              <CrceButton
                label="Bulk Upload"
                variant="tertiary"
                size="small"
                icon={UploadIcon}
                onClick={!isWriteAccess ? showBulkUploadModal : undefined}
                disabled={isWriteAccess ? true : undefined}
              />
              <CrceButton
                label="Download Excel"
                variant="tertiary"
                size="small"
                icon={Download}
                onClick={handleDownloadData}
                disabled={downloadLoading ? true : undefined}
              />
              {selectedRows.length > 0 && (
                <CrceButton
                  label={`Combined Report (${selectedRows.length})`}
                  variant="tertiary"
                  size="small"
                  icon={pdfGenerationLoading ? undefined : FileText}
                  onClick={!isWriteAccess ? handleGeneratePdf : undefined}
                  disabled={(isWriteAccess || pdfGenerationLoading) ? true : undefined}
                />
              )}
              <CrceButton
                label="Add New"
                variant="primary"
                size="small"
                icon={Plus}
                onClick={!isWriteAccess ? showModal : undefined}
                disabled={isWriteAccess ? true : undefined}
              />
            </div>
          </div>

          <div className="mc-toolbar">
            <div className="mc-search-wrapper">
              <Search size={16} className="mc-search-icon" />
              <input
                type="text"
                className="mc-search-input"
                placeholder="Search material consumption..."
                value={searchKeyExist}
                onChange={handleMcSearchChange}
              />
            </div>
            <div className="mc-toolbar-right">
              {/* Optional result mapping info */}
            </div>
          </div>
        </>
      )}
      <div className="mc-table-container dpr-settings-pipelines-table-wrap">
        {materialConsumption.length ? (
          <Table
            pagination={{
              position: ['bottomCenter'],
              className: 'heat-profile-pagination',
              current: mcPage,
              pageSize: mcPageSize,
              total: filteredMaterialConsumption.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) =>
                total === 0 ? `0 of ${total}` : `${range[0]}–${range[1]} of ${total}`,
              onChange: (page, size) => {
                setMcPage(page);
                if (size != null && size !== mcPageSize) {
                  setMcPageSize(size);
                  setMcPage(1);
                }
              },
            }}
            columns={columns}
            dataSource={paginatedMaterialConsumption}
            className="material-consumption-table project-tab-heat-table"
            scroll={{ x: MC_TABLE_SCROLL_X }}
            loading={isMaterialConsumptionsLoading}
            rowSelection={
              filteredMaterialConsumption.length ? materialConsumptionRowSelection : undefined
            }
            rowKey={materialConsumptionRowKey}
            rowClassName={() => 'material-consumption-row'}
            locale={{ emptyText: <ProjectTabTableEmpty /> }}
          />
        ) : (
          <Table
            pagination={false}
            columns={columns}
            dataSource={materialConsumption}
            loading={isMaterialConsumptionsLoading}
            className="material-consumption-table project-tab-heat-table"
            locale={{ emptyText: <ProjectTabTableEmpty /> }}
          />
        )}
      </div>
      {bulkUplaodModal && (
        <div
          className="mc-dpr-modal-backdrop"
          role="presentation"
          onClick={() =>
            !isUploadConsumablesAndMaterialConsumptions && handleCancelUploadFile()
          }
        >
          <div
            className="mc-dpr-modal mc-dpr-modal--narrow"
            role="dialog"
            aria-labelledby="mc-bulk-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mc-dpr-modal-head">
              <h3 id="mc-bulk-modal-title" className="mc-dpr-modal-title">
                Bulk upload
              </h3>
              <button
                type="button"
                className="mc-dpr-modal-close mc-dpr-modal-close--emphasized"
                aria-label="Close"
                disabled={isUploadConsumablesAndMaterialConsumptions}
                onClick={handleCancelUploadFile}
              >
                <X size={22} strokeWidth={2.25} aria-hidden />
              </button>
            </div>
            <div className="mc-dpr-modal-body">
              <p className="mc-dpr-modal-intro">
                Select an Excel file (.xlsx or .xls) created from the material consumption template.
              </p>
              <div className="mc-bulk-upload-controls">
                <div className="mc-dpr-modal-upload-wrap">
                  <Upload
                    {...fileProps}
                    maxCount={1}
                    showUploadList={false}
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  >
                    <CrceButton
                      label="Select file"
                      variant="tertiary"
                      size="medium"
                      icon={UploadIcon}
                      disabled={disableFileUpload || isUploadConsumablesAndMaterialConsumptions}
                    />
                  </Upload>
                </div>
                {fileList.length > 0 ? (
                  <div
                    className="mc-dpr-modal-selected-file-card"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="mc-dpr-modal-selected-file-main">
                      <FileText size={18} strokeWidth={2} aria-hidden />
                      <span
                        className="mc-dpr-modal-selected-file-name"
                        title={bulkUploadFileDisplayName}
                      >
                        {bulkUploadFileDisplayName}
                      </span>
                      {bulkUploadFileSizeLabel ? (
                        <span className="mc-dpr-modal-selected-file-size">
                          {bulkUploadFileSizeLabel}
                        </span>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="mc-dpr-modal-selected-file-remove"
                      aria-label={`Remove ${bulkUploadFileDisplayName}`}
                      disabled={isUploadConsumablesAndMaterialConsumptions}
                      onClick={() => fileProps.onRemove(bulkUploadAntFile)}
                    >
                      <X size={16} aria-hidden />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="mc-dpr-modal-foot">
              <CrceButton
                label="Cancel"
                variant="tertiary"
                size="medium"
                onClick={handleCancelUploadFile}
                disabled={isUploadConsumablesAndMaterialConsumptions}
              />
              <CrceButton
                label={isUploadConsumablesAndMaterialConsumptions ? 'Uploading…' : 'Upload'}
                variant="primary"
                size="medium"
                onClick={handleUploadFile}
                disabled={isUploadConsumablesAndMaterialConsumptions}
              />
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="mc-dpr-modal-backdrop" role="presentation">
          <div
            className="mc-dpr-modal mc-dpr-modal--wide"
            role="dialog"
            aria-labelledby="mc-add-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mc-dpr-modal-head">
              <h3 id="mc-add-modal-title" className="mc-dpr-modal-title">
                {editValue ? 'Edit material consumption' : 'Add material consumption'}
              </h3>
              <button type="button" className="mc-dpr-modal-close" aria-label="Close" onClick={handleCancel}>
                <X size={20} />
              </button>
            </div>
            <div className="mc-dpr-modal-body">
              <Form form={editDetailsForm} name="basic" layout="vertical" autoComplete="off" colon={false}>
                {editValue ? (
                  <div className="mc-dpr-modal-edit-cert">
                    {certificate !== undefined ? (
                      <a href={certificate} target="_blank" rel="noreferrer" className="mc-dpr-modal-cert-link">
                        View current certificate
                      </a>
                    ) : (
                      <span className="mc-dpr-modal-cert-muted">No certificate uploaded</span>
                    )}
                  </div>
                ) : null}
                <div className="mc-dpr-modal-form-grid">
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="mc-field-category">
                        Material category <span className="required">*</span>
                      </label>
                      <Form.Item
                        name="category"
                        style={{ marginBottom: 0 }}
                        rules={[{ required: true, message: 'Material category is required' }]}
                      >
                        <Select
                          id="mc-field-category"
                          dropdownClassName="modern-dropdown-popup mc-dpr-modal-select-dd"
                          placeholder="Material category"
                          showSearch
                          allowClear
                          optionFilterProp="children"
                          onChange={onChangeCategory}
                        >
                          {categoryDataList &&
                            categoryDataList.map((item, i) => (
                              <Option key={i} value={item && item.category}>
                                {item && item.category}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="mc-field-name">
                        Name <span className="required">*</span>
                      </label>
                      <Form.Item
                        name="name"
                        style={{ marginBottom: 0 }}
                        rules={[{ required: true, message: 'Name is required' }]}
                      >
                        <Select
                          id="mc-field-name"
                          dropdownClassName="modern-dropdown-popup mc-dpr-modal-select-dd"
                          placeholder="Name"
                          showSearch
                          allowClear
                          optionFilterProp="children"
                          onChange={onChangeName}
                          disabled={!filteredCatg.length}
                        >
                          {filteredCatg &&
                            filteredCatg.map((item, i) => (
                              <Option key={i} value={item && item.name}>
                                {item && item.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="mc-field-product">
                        Product code <span className="required">*</span>
                      </label>
                      <Form.Item
                        name="productCode"
                        style={{ marginBottom: 0 }}
                        rules={[{ required: true, message: 'Product code is required' }]}
                      >
                        <Select
                          id="mc-field-product"
                          dropdownClassName="modern-dropdown-popup mc-dpr-modal-select-dd"
                          placeholder="Product code"
                          showSearch
                          allowClear
                          optionFilterProp="children"
                          onChange={(value) => setProductCode(value)}
                          disabled={!name}
                        >
                          {filteredProductCode &&
                            filteredProductCode.map((item, i) => (
                              <Option key={i} value={item && item.productCode}>
                                {item && item.productCode}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="mc-field-batch">
                        Batch number <span className="required">*</span>
                      </label>
                      <Form.Item
                        name="batchNumber"
                        style={{ marginBottom: 0 }}
                        rules={[{ required: true, message: 'Batch number is required' }]}
                      >
                        <Input id="mc-field-batch" placeholder="Batch number" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="mc-field-qty">
                        Initial quantity <span className="required">*</span>
                      </label>
                      <Form.Item
                        name="totalMobStockQty"
                        style={{ marginBottom: 0 }}
                        rules={[{ required: true, message: 'Initial quantity is required' }]}
                      >
                        <Input id="mc-field-qty" placeholder="Initial quantity" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="mc-field-safety">
                        Safety stock <span className="required">*</span>
                      </label>
                      <Form.Item
                        name="safetyStock"
                        style={{ marginBottom: 0 }}
                        rules={[{ required: true, message: 'Safety stock is required' }]}
                      >
                        <Input id="mc-field-safety" placeholder="Safety stock" />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="mc-field-expiry">Expiry date</label>
                      <Form.Item name="expiryDate" style={{ marginBottom: 0 }}>
                        <DatePicker
                          id="mc-field-expiry"
                          dropdownClassName="modern-dropdown-popup mc-dpr-datepicker-dropdown"
                          format="YYYY-MM-DD"
                          inputReadOnly
                          style={{ width: '100%' }}
                          onChange={(date, dateString) => {
                            setExpiryDate(dateString);
                          }}
                        />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row">
                    <div className="mc-dpr-modal-field">
                      <label htmlFor="mc-field-unit">
                        Unit <span className="required">*</span>
                      </label>
                      <Form.Item
                        name="unit"
                        style={{ marginBottom: 0 }}
                        rules={[{ required: true, message: 'Unit is required' }]}
                      >
                        <Select
                          id="mc-field-unit"
                          dropdownClassName="modern-dropdown-popup mc-dpr-modal-select-dd"
                          placeholder="Select unit"
                          showSearch
                          allowClear
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {categoryDataList &&
                            categoryDataList.map((item, i) => (
                              <Option key={i} value={item && item.unit}>
                                {item && item.unit}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="mc-dpr-modal-form-row mc-dpr-modal-form-row--span-2">
                    <div className="mc-dpr-modal-field">
                      <label>Material certificate</label>
                      <Form.Item name="certificate" style={{ marginBottom: 0 }}>
                        <Upload {...filePropsForOne} maxCount={1} showUploadList={false}>
                          <CrceButton
                            label="Select file"
                            variant="tertiary"
                            size="medium"
                            icon={UploadIcon}
                            disabled={certificateUpload}
                          />
                        </Upload>
                        {selectedCertificateFileName ? (
                          <div className="mc-dpr-modal-selected-file-card">
                            <div
                              className="mc-dpr-modal-selected-file-main"
                              title={selectedCertificateFileName}
                            >
                              <FileText size={14} />
                              <span className="mc-dpr-modal-selected-file-name">
                                {selectedCertificateFileName}
                              </span>
                              {selectedCertificateFileSize ? (
                                <span className="mc-dpr-modal-selected-file-size">
                                  {selectedCertificateFileSize}
                                </span>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              className="mc-dpr-modal-selected-file-remove"
                              onClick={handleRemoveSelectedCertificate}
                              aria-label="Remove selected file"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : null}
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
            <div className="mc-dpr-modal-foot">
              <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={handleCancel} />
              <CrceButton
                label={
                  !editValue
                    ? isMaterialConsumptionsLoading
                      ? 'Adding…'
                      : 'Add'
                    : isMaterialConsumptionsUpdating
                    ? 'Updating…'
                    : 'Update'
                }
                variant="primary"
                size="medium"
                onClick={handleAdd}
                disabled={isMaterialConsumptionsLoading || isMaterialConsumptionsUpdating}
              />
            </div>
          </div>
        </div>
      )}
      {isDeleteModalVisible &&
        selectedData &&
        typeof selectedData === 'object' &&
        selectedData.id != null && (
          <div
            className="mc-dpr-modal-backdrop"
            role="presentation"
            onClick={() => !isMaterialConsumptionDeleteLoading && handleDeleteCancel()}
          >
            <div
              className="mc-dpr-modal mc-dpr-modal--narrow mc-dpr-modal--delete"
              role="dialog"
              aria-labelledby="mc-delete-modal-title"
              aria-describedby="mc-delete-modal-desc"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mc-dpr-modal-head">
                <h3 id="mc-delete-modal-title" className="mc-dpr-modal-title">
                  Delete material consumption
                </h3>
                <button
                  type="button"
                  className="mc-dpr-modal-close"
                  aria-label="Close"
                  disabled={isMaterialConsumptionDeleteLoading}
                  onClick={handleDeleteCancel}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="mc-dpr-modal-body">
                <div id="mc-delete-modal-desc" className="mc-dpr-modal-delete-body">
                  <p className="mc-dpr-modal-delete-lead">
                    Are you sure you want to delete this material consumption?
                  </p>
                  <p className="mc-dpr-modal-delete-target">
                    {selectedData.name} — {selectedData.productCode}
                  </p>
                  <p className="mc-dpr-modal-delete-warning">This action cannot be undone.</p>
                </div>
              </div>
              <div className="mc-dpr-modal-foot">
                <CrceButton
                  label="Cancel"
                  variant="tertiary"
                  size="medium"
                  onClick={handleDeleteCancel}
                  disabled={isMaterialConsumptionDeleteLoading}
                />
                <span className="mc-delete-confirm-wrap">
                  <CrceButton
                    label={isMaterialConsumptionDeleteLoading ? 'Deleting…' : 'Delete'}
                    variant="primary"
                    size="medium"
                    onClick={handleDeleteOk}
                    disabled={isMaterialConsumptionDeleteLoading}
                  />
                </span>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

const mapStateToProps = ({ materialConsumptionReducer, consumableReducer }) => {
  const materialConsumption = get(materialConsumptionReducer, 'materialConsumptions', []);
  const isMaterialConsumptionsLoading = get(
    materialConsumptionReducer,
    'isMaterialConsumptionsLoading',
    []
  );
  const isMaterialConsumptionDeleteLoading = get(
    materialConsumptionReducer,
    'isMaterialConsumptionDeleteLoading',
    false
  );
  const isUploadConsumablesAndMaterialConsumptions = get(
    consumableReducer,
    'isUploadConsumablesAndMaterialConsumptions'
  );
  const isMaterialConsumptionsUpdating = get(
    materialConsumptionReducer,
    'isMaterialConsumptionsUpdating',
    false
  );
  const categoryData = get(materialConsumptionReducer, 'materialCategories', []);

  return {
    materialConsumption,
    isMaterialConsumptionsLoading,
    isMaterialConsumptionDeleteLoading,
    isUploadConsumablesAndMaterialConsumptions,
    isMaterialConsumptionsUpdating,
    categoryData,
  };
};

const mapDispatchToProps = {
  onFetchMaterialConsumption: fetchMaterialConsumption,
  onAddMaterialConsumption: addMaterialConsumption,
  onUploadConsumablesAndMaterialFiles: uploadConsumablesAndMaterialFile,
  onDeleteMaterialConsumption: deleteMaterilaConsumption,
  onUpdateMaterialConsumption: updateMaterialConsumption,
  onFetchCategories: fetchMaterialCategories,
};

export default connect(mapStateToProps, mapDispatchToProps)(MaterialConsumption);
